import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, whatsappUrl } from "../../config/contact";

export function Contact() {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.open(whatsappUrl(undefined, message));
  };

  const infoCards = [
    { icon: Phone, bg: "bg-primary/10", color: "text-primary", title: t("contact.phone"), lines: [CONTACT_PHONE_DISPLAY] },
    { icon: Mail, bg: "bg-blue-100", color: "text-blue-600", title: t("contact.email"), lines: [CONTACT_EMAIL] },
    { icon: MapPin, bg: "bg-orange-100", color: "text-orange-500", title: t("contact.address"), lines: [t("contact.addressLine1"), t("contact.addressLine2")] },
  ];

  return (
    <section id="contact" className="bg-slate-50 py-16 sm:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-10 text-center sm:mb-16">
          <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
            {t("contact.title")} <span className="text-primary">{t("contact.highlight")}</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">{t("contact.subtitle")}</p>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <Card className="border-0 shadow-xl ring-1 ring-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl">{t("contact.formTitle")}</CardTitle>
              <CardDescription>{t("contact.formDescription")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <label htmlFor="contact-message" className="sr-only">
                  {t("contact.messagePlaceholder")}
                </label>
                <Textarea
                  id="contact-message"
                  placeholder={t("contact.messagePlaceholder")}
                  rows={5}
                  maxLength={1000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  className="resize-none border-slate-200 focus:border-primary focus:ring-primary"
                />
                <Button type="submit" className="h-12 w-full bg-primary text-base hover:bg-primary/90">
                  {t("contact.send")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-4 sm:space-y-6">
            {infoCards.map((card) => (
              <Card key={card.title} className="border-0 bg-white shadow-lg ring-1 ring-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${card.bg}`}>
                      <card.icon className={`h-6 w-6 ${card.color}`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="mb-1 text-lg font-bold text-slate-900">{card.title}</h3>
                      {card.lines.map((line) => (
                        <p key={line} className="break-words text-slate-600">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
